import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import type { AppState } from "ee/reducers";
import type { AIChatState } from "ee/reducers/uiReducers/aiChatReducer";
import { commandService, type CommandType } from "../services/CommandService";
import { initialAIChatState } from "ee/reducers/uiReducers/aiChatReducer";

export interface UseAIChatCoordinator {
  state: AIChatState;
  updateState: (updates: Partial<AIChatState>) => void;
  startOperation: () => AbortSignal;
  stopOperation: () => void;
}

/**
 * Hook for coordinating AI chat operations state using Redux
 * @param widgetId The widget ID for which to manage state
 * @returns State and operations for AI chat coordination
 */
export function useAIChatCoordinator(widgetId: string): UseAIChatCoordinator {
  const dispatch = useDispatch();
  const state = useSelector(
    (appState: AppState) => appState.ui.aiChat[widgetId],
  );

  useEffect(
    function initializeAIChatStateIfNeeded() {
      if (state === undefined) {
        dispatch({
          type: ReduxActionTypes.AI_CHAT_SET_STATE,
          payload: { widgetId, state: initialAIChatState },
        });
      }
    },
    [state, widgetId],
  );

  const updateState = useCallback(
    (updates: Partial<AIChatState>) => {
      dispatch({
        type: ReduxActionTypes.AI_CHAT_SET_STATE,
        payload: { widgetId, state: updates },
      });
    },
    [dispatch, widgetId],
  );

  // Subscribe to commands
  useEffect(
    function subscribeToCommands() {
      const handleCommand = (command: CommandType) => {
        if (command === "OPERATION_STOPPED") {
          updateState({
            isProcessing: false,
            isWaitingForResponse: false,
          });
        } else if (command === "OPERATION_STARTED") {
          updateState({
            isProcessing: true,
            isCancelling: false,
          });
        }
      };

      const unsubscribe = commandService.subscribe(handleCommand);

      return unsubscribe;
    },
    [updateState],
  );

  const startOperation = useCallback((): AbortSignal => {
    dispatch({
      type: ReduxActionTypes.AI_CHAT_SET_STATE,
      payload: { widgetId, state: { isProcessing: true } },
    });

    return commandService.startOperation();
  }, [dispatch, widgetId]);

  const stopOperation = useCallback((): void => {
    commandService.stopOperation();
  }, []);

  return {
    state: state ?? {},
    updateState,
    startOperation,
    stopOperation,
  };
}
